import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ordenesApi, clientesApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { TableSkeleton } from '../lib/skeletons';
import { toast } from 'sonner';
import { Plus, Search, Filter, FileText, Pencil, Trash2, Eye, X, Printer } from 'lucide-react';

const AREAS = ['FABRICACION', 'MONTAJE', 'OBRAS CIVILES', 'RILES', 'ARRIENDO MAQ', 'TRANSPORTE', 'NO PRODUCTIVO'];

// Celda de avance (%) editable inline. Guarda con PATCH solo si cambia el valor.
const AvanceCell = ({ ot }) => {
  const [val, setVal] = useState(ot.avance ?? 0);
  const [saving, setSaving] = useState(false);
  const pct = Math.max(0, Math.min(100, parseFloat(val) || 0));

  const save = async () => {
    const num = Math.max(0, Math.min(100, parseFloat(val) || 0));
    if (num === (ot.avance ?? 0)) return;
    setSaving(true);
    try {
      await ordenesApi.patch(ot.id, { avance: num });
      ot.avance = num;
      toast.success(`Avance OT ${ot.numero_ot}: ${num}%`);
    } catch (_e) {
      toast.error('Error al guardar avance');
      setVal(ot.avance ?? 0);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-1.5">
      <div className="w-14 h-1.5 bg-gray-200 rounded-full overflow-hidden hidden xl:block">
        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
      </div>
      <input
        type="number"
        min="0"
        max="100"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
        disabled={saving}
        className="w-12 text-xs text-right border rounded px-1 py-0.5 font-mono disabled:opacity-50"
        data-testid={`avance-input-${ot.numero_ot}`}
      />
      <span className="text-xs text-muted-foreground">%</span>
    </div>
  );
};

export const OrdenesTrabajoList = () => {
  const [ordenes, setOrdenes] = useState([]);
  const [allOrdenes, setAllOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ 
    mostrarTerminadas: false,
    area: '', 
    cliente_id: '' 
  });
  const [showFilters, setShowFilters] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [clientes, setClientes] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();
  // Permiso para ver montos: admin, gerencia, contabilidad, o usuarios con `acciones.ots_ver_montos = true`
  const canVerMontos = (
    ['admin', 'gerencia', 'contabilidad'].includes(user?.rol) ||
    user?.permisos?.informacion?.acciones?.ots_ver_montos === true ||
    user?.permisos?.informacion?.editar === true
  );

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, allOrdenes]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [ordenesRes, clientesRes] = await Promise.all([
        ordenesApi.getAll({}),
        clientesApi.getAll(),
      ]);
      
      setAllOrdenes(ordenesRes.data);
      setClientes(clientesRes.data.sort((a, b) => 
        (a.razon_social || '').localeCompare(b.razon_social || '')
      ));
    } catch (error) {

      toast.error('Error al cargar las órdenes de trabajo');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let data = [...allOrdenes];
    
    // Filtrar por estado de ejecución
    if (!filters.mostrarTerminadas) {
      data = data.filter(ot => ot.estado === 'EN EJECUCION');
    }
    
    // Filtrar por área
    if (filters.area) {
      data = data.filter(ot => ot.area === filters.area);
    }
    
    // Filtrar por cliente
    if (filters.cliente_id) {
      data = data.filter(ot => ot.cliente_id === filters.cliente_id);
    }
    
    // Ordenar por cliente y luego por número de OT
    data.sort((a, b) => {
      const clienteA = (a.cliente_nombre || '').toLowerCase();
      const clienteB = (b.cliente_nombre || '').toLowerCase();
      if (clienteA !== clienteB) return clienteA.localeCompare(clienteB);
      return a.numero_ot - b.numero_ot;
    });
    
    setOrdenes(data);
  };

  const handleDelete = async (id) => {
    try {
      await ordenesApi.delete(id);
      toast.success('Orden de trabajo eliminada');
      loadData();
    } catch (_error) {
      toast.error('Error al eliminar la orden');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const clearFilters = () => {
    setFilters({ mostrarTerminadas: false, area: '', cliente_id: '' });
    setSearchTerm('');
  };

  const handlePrint = () => {
    window.print();
  };

  const getPrintTitle = () => {
    const parts = ['Órdenes de Trabajo'];
    if (!filters.mostrarTerminadas) {
      parts.push('EN EJECUCIÓN');
    } else {
      parts.push('TODAS');
    }
    return parts.join(' - ');
  };

  const filteredOrdenes = ordenes.filter((ot) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      ot.numero_ot?.toString().includes(search) ||
      ot.descripcion?.toLowerCase().includes(search) ||
      ot.cliente_nombre?.toLowerCase().includes(search) ||
      ot.proyecto_codigo?.toLowerCase().includes(search)
    );
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(value || 0);
  };

  const hasActiveFilters = filters.area || filters.cliente_id || searchTerm;
  
  const countEnEjecucion = allOrdenes.filter(ot => ot.estado === 'EN EJECUCION').length;
  const countTerminadas = allOrdenes.filter(ot => ot.estado !== 'EN EJECUCION').length;

  if (loading) {
    return <TableSkeleton rows={10} cols={8} />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ordenes-trabajo-list">
      {/* Print Header */}
      <div className="hidden print:block print:mb-4">
        <div className="text-center border-b-2 border-black pb-2 mb-2">
          <h1 className="text-xl font-bold">CHIRINO STEEL</h1>
          <h2 className="text-lg font-semibold">{getPrintTitle()}</h2>
          <p className="text-sm text-gray-600">Fecha: {new Date().toLocaleDateString('es-CL')} | Total: {filteredOrdenes.length}</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 print:hidden">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Órdenes de Trabajo</h1>
          <p className="text-muted-foreground">
            {filters.mostrarTerminadas 
              ? `${ordenes.length} órdenes (${countEnEjecucion} en ejecución, ${countTerminadas} terminadas)`
              : `${ordenes.length} órdenes en ejecución`}
            {' - Ordenado por Cliente'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild data-testid="new-ot-btn">
            <Link to="/ordenes-trabajo/nueva"><Plus className="w-4 h-4 mr-2" />Nueva OT</Link>
          </Button>
          <Button variant="outline" onClick={handlePrint} data-testid="print-btn">
            <Printer className="w-4 h-4 mr-2" />Imprimir
          </Button>
        </div>
      </div>

      {deleteConfirm && (
        <Card className="border-2 border-destructive/50 bg-destructive/5 print:hidden">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">¿Eliminar OT {deleteConfirm.numero_ot}?</p>
                <p className="text-sm text-muted-foreground">Esta acción no se puede deshacer.</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
                <Button variant="destructive" size="sm" onClick={() => handleDelete(deleteConfirm.id)}>Eliminar</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="print:hidden">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input 
                placeholder="Buscar por número, descripción, cliente..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                className="pl-10" 
              />
            </div>
            <Button 
              variant={filters.mostrarTerminadas ? 'default' : 'outline'} 
              onClick={() => setFilters({...filters, mostrarTerminadas: !filters.mostrarTerminadas})}
            >
              <Eye className="w-4 h-4 mr-2" />
              {filters.mostrarTerminadas ? 'Mostrando Todas' : 'Ver Terminadas'}
            </Button>
            <Button 
              variant={showFilters ? 'default' : 'outline'} 
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4 mr-2" />Filtros
            </Button>
          </div>
          
          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 pt-4 border-t">
              <div>
                <label className="text-sm font-medium mb-2 block">Área</label>
                <select 
                  value={filters.area} 
                  onChange={(e) => setFilters({ ...filters, area: e.target.value })} 
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Todas</option>
                  {AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Cliente</label>
                <select 
                  value={filters.cliente_id} 
                  onChange={(e) => setFilters({ ...filters, cliente_id: e.target.value })} 
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Todos</option>
                  {clientes.map((c) => <option key={c.id} value={c.id}>{c.razon_social}</option>)}
                </select>
              </div>
              <div className="flex items-end">
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    <X className="w-4 h-4 mr-2" />Limpiar
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {filteredOrdenes.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground text-sm">No se encontraron órdenes de trabajo</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="table-compact">
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="py-2">N° OT</TableHead>
                    <TableHead className="py-2">Proy</TableHead>
                    <TableHead className="py-2">Cliente</TableHead>
                    <TableHead className="hidden md:table-cell py-2">Descripción</TableHead>
                    <TableHead className="py-2 text-center">Estado</TableHead>
                    <TableHead className="py-2 text-center">Avance</TableHead>
                    <TableHead className="hidden lg:table-cell py-2">Área</TableHead>
                    {canVerMontos && (
                      <TableHead className="hidden lg:table-cell text-right py-2">Monto</TableHead>
                    )}
                    <TableHead className="w-24 py-2 print:hidden">Acc.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrdenes.map((ot) => {
                    const esEnEjecucion = ot.estado === 'EN EJECUCION';
                    
                    return (
                      <TableRow 
                        key={ot.id} 
                        className={`hover:bg-muted/30 ${!esEnEjecucion ? 'bg-gray-50' : ''}`}
                      >
                        <TableCell className="font-mono font-medium py-1">
                          {ot.numero_ot}
                          {ot.es_aumento_obra && <span className="text-orange-600 text-xs ml-1">(AO)</span>}
                        </TableCell>
                        <TableCell className="font-mono text-xs py-1">{ot.proyecto_codigo}</TableCell>
                        <TableCell className="max-w-[120px] truncate text-xs py-1">{ot.cliente_nombre}</TableCell>
                        <TableCell className="hidden md:table-cell max-w-[180px] truncate text-muted-foreground text-xs py-1">
                          {ot.descripcion}
                        </TableCell>
                        <TableCell className="py-1 text-center">
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-medium ${
                              esEnEjecucion
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-gray-200 text-gray-600'
                            }`}
                          >
                            {esEnEjecucion ? 'EN EJECUCION' : 'TERMINADA'}
                          </span>
                        </TableCell>
                        <TableCell className="py-1 text-center" data-testid={`avance-cell-${ot.numero_ot}`}>
                          <AvanceCell ot={ot} />
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-xs py-1">{ot.area}</TableCell>
                        {canVerMontos && (
                          <TableCell className="hidden lg:table-cell text-right font-mono text-xs py-1">
                            {formatCurrency(ot.monto)}
                          </TableCell>
                        )}
                        <TableCell className="py-1 print:hidden">
                          <div className="flex gap-0.5">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navigate(`/ordenes-trabajo/${ot.id}`)}>
                              <Eye className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navigate(`/ordenes-trabajo/${ot.id}/editar`)}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDeleteConfirm(ot)}>
                              <Trash2 className="w-3.5 h-3.5 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Leyenda */}
      <div className="text-xs text-muted-foreground print:hidden">
        <p><span className="bg-green-100 text-green-700 px-1 rounded">EN EJECUCION</span> OT con gastos activos | <span className="bg-gray-200 text-gray-600 px-1 rounded">TERMINADA</span> OT completada</p>
      </div>
    </div>
  );
};
